<template>
  <div class="min-h-screen" data-theme="amber">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="shops" />

    <!-- Content -->
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('shops.title') }}</h1>
        <button v-if="signedIn" @click="openCreateModal" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">
          {{ t('shops.create') }}
        </button>
      </div>

      <!-- Filters -->
      <div class="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('shops.filter_category') }}:</label>
            <select v-model="filterCategory" class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{{ t('shops.filter_all') }}</option>
              <option value="wear">{{ t('shops.category_wear') }}</option>
              <option value="nutrition">{{ t('shops.category_nutrition') }}</option>
              <option value="equipment">{{ t('shops.category_equipment') }}</option>
              <option value="other">{{ t('shops.category_other') }}</option>
            </select>
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('shops.filter_search') }}:</label>
            <input v-model="filterSearch" type="text" :placeholder="t('shops.filter_search_placeholder')"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 w-48 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('shops.filter_brand') }}:</label>
            <select v-model="filterBrand" class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{{ t('shops.filter_all') }}</option>
              <option v-for="b in availableBrands" :key="b" :value="b">{{ b }}</option>
            </select>
          </div>

          <span class="text-xs text-gray-400 ml-auto">{{ filteredShops.length }} / {{ shops.length }}</span>
        </div>
      </div>

      <!-- Map -->
      <div v-show="allLocations.length > 0" class="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden map-container">
        <div ref="mapContainer" style="height: 320px; width: 100%;"></div>
      </div>

      <!-- Flash -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- List -->
      <div v-if="filteredShops.length === 0" class="text-center py-12 text-gray-500">
        {{ t('shops.no_shops') }}
      </div>

      <div class="grid gap-4">
        <div v-for="shop in filteredShops" :key="shop.id" @click="goToShop($event, shop)" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition cursor-pointer">
          <div class="flex items-center justify-between gap-4">
            <img v-if="shop.logo_url" :src="shop.logo_url" class="w-16 h-16 rounded-lg object-cover border border-gray-200 shrink-0" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-2">
                <a :href="`/shops/${shop.id}`" class="text-lg font-semibold text-gray-900 hover:text-blue-700">{{ shop.name }}</a>
                <span v-if="shop.category" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ t('shops.category_' + shop.category) }}
                </span>
                <span v-if="shop.pending" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {{ t('shops.pending') }}
                </span>
              </div>
              <div v-if="shop.brands && shop.brands.length > 0" class="flex flex-wrap gap-1.5 mb-2">
                <span v-for="b in shop.brands" :key="b" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{{ b }}</span>
              </div>
              <div v-if="shop.locations && shop.locations.length > 0" class="text-xs text-gray-400 mb-1">
                <p v-for="loc in shop.locations" :key="loc.id">{{ [loc.name, loc.address].filter(Boolean).join(' — ') }}</p>
              </div>
              <div v-if="shop.link" class="mt-1" @click.stop>
                <a :href="shop.link" target="_blank" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition">
                  {{ t('shops.visit_link') }}
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                </a>
              </div>
            </div>

            <div v-if="userAdmin || shop.user_id === userId" class="flex flex-col gap-2 ml-4" @click.stop>
              <button v-if="userAdmin && shop.pending" @click="approveItem(shop)" class="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition">{{ t('shops.approve') }}</button>
              <button @click="openEditModal(shop)" class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">{{ t('shops.edit') }}</button>
              <button @click="confirmDelete(shop)" class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">{{ t('shops.delete') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 class="text-lg font-bold text-gray-900 mb-6">{{ editing ? t('shops.edit_shop') : t('shops.new_shop') }}</h2>

        <div v-if="formErrors.length" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p v-for="error in formErrors" :key="error" class="text-sm text-red-700">{{ error }}</p>
        </div>

        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shops.name') }}</label>
            <input v-model="form.name" type="text" required class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shops.category') }}</label>
            <select v-model="form.category" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm">
              <option value="">—</option>
              <option value="wear">{{ t('shops.category_wear') }}</option>
              <option value="nutrition">{{ t('shops.category_nutrition') }}</option>
              <option value="equipment">{{ t('shops.category_equipment') }}</option>
              <option value="other">{{ t('shops.category_other') }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shops.brands') }}</label>
            <div class="flex flex-wrap gap-1.5 mb-2">
              <span v-for="(b, idx) in form.brands" :key="idx" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {{ b }}
                <button type="button" @click="removeBrand(idx)" class="text-amber-700 hover:text-amber-900">&times;</button>
              </span>
            </div>
            <input v-model="brandInput" type="text" :placeholder="t('shops.brand_placeholder')"
                   @keydown.enter.prevent="addBrand"
                   @keydown="onBrandKey"
                   @blur="addBrand"
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
            <p class="text-xs text-gray-400 mt-1">{{ t('shops.brand_hint') }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shops.link') }}</label>
            <input v-model="form.link" type="url" placeholder="https://..." class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('shops.description') }}</label>
            <textarea v-model="form.description" rows="3" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"></textarea>
          </div>
          <ImageUpload v-model="form.logo_url" :label="t('shops.logo')" :upload-text="t('shops.upload_logo')" :change-text="t('shops.change_logo')" :remove-text="t('shops.remove_logo')" :csrf-token="csrfToken" upload-type="shops" />

          <!-- Locations -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">{{ t('shops.locations') }}</label>
              <button type="button" @click="addLocation" class="text-xs text-blue-700 hover:text-blue-900 font-medium">+ {{ t('shops.add_location') }}</button>
            </div>
            <div v-for="(loc, idx) in form.locations" :key="idx" class="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-center justify-between mb-2">
                <input v-model="loc.name" type="text" :placeholder="t('shops.location_name')" class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                <button type="button" @click="removeLocation(idx)" class="ml-2 text-red-400 hover:text-red-600 text-sm">&times;</button>
              </div>
              <div class="mb-2">
                <input v-model="loc.address" type="text" :placeholder="t('shops.address')" class="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div :ref="el => { if (el) formMapRefs[idx] = el }" style="height: 150px; width: 100%;" class="rounded border border-gray-300 mb-2"></div>
              <div class="grid grid-cols-2 gap-2">
                <input v-model="loc.latitude" type="number" step="0.0000001" readonly :placeholder="t('shops.latitude')" class="px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs" />
                <input v-model="loc.longitude" type="number" step="0.0000001" readonly :placeholder="t('shops.longitude')" class="px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs" />
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3 pt-4">
            <button type="button" @click="closeModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('shops.cancel') }}</button>
            <button type="submit" :disabled="submitting" class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ submitting ? '...' : (editing ? t('shops.save') : t('shops.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeDeleteModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ t('shops.confirm_delete') }}</h2>
        <p class="text-sm text-gray-600 mb-6">{{ t('shops.confirm_delete_message') }}</p>
        <div class="flex justify-end gap-3">
          <button @click="closeDeleteModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('shops.cancel') }}</button>
          <button @click="deleteShop" :disabled="submitting" class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">
            {{ submitting ? '...' : t('shops.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import ImageUpload from './ImageUpload.vue'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, shops: Array, csrfToken: String })
const { t, locale } = useI18n()

const items = ref([...props.shops])
const filterCategory = ref('')
const filterSearch = ref('')
const filterBrand = ref('')
const brandInput = ref('')
const showModal = ref(false)
const showDeleteModal = ref(false)
const editing = ref(false)
const editingId = ref(null)
const submitting = ref(false)
const formErrors = ref([])
const deleteTarget = ref(null)
const flash = ref(null)
const mapContainer = ref(null)
const formMapRefs = ref({})
let map = null, markers = []
let formMaps = {}, formMarkers = {}

const form = reactive({ name: '', category: '', link: '', description: '', logo_url: '', brands: [], locations: [] })

const availableBrands = computed(() => {
  const set = new Set()
  items.value.forEach(s => (s.brands || []).forEach(b => set.add(b)))
  return [...set].sort((a, b) => a.localeCompare(b))
})

const filteredShops = computed(() => {
  return items.value.filter(s => {
    if (filterCategory.value && s.category !== filterCategory.value) return false
    if (filterBrand.value && !(s.brands || []).includes(filterBrand.value)) return false
    if (filterSearch.value) {
      const q = filterSearch.value.toLowerCase()
      const addrMatch = (s.locations || []).some(l => (l.address || '').toLowerCase().includes(q) || (l.name || '').toLowerCase().includes(q))
      const brandMatch = (s.brands || []).some(b => b.toLowerCase().includes(q))
      if (!s.name.toLowerCase().includes(q) && !addrMatch && !brandMatch) return false
    }
    return true
  })
})

const allLocations = computed(() => {
  const locs = []
  filteredShops.value.forEach(s => {
    if (s.locations) s.locations.forEach(l => { if (l.latitude && l.longitude) locs.push({ ...l, shop: s }) })
  })
  return locs
})

function goToShop(e, shop) { window.location.href = `/shops/${shop.id}` }

function showFlash(msg, type = 'success') { flash.value = { message: msg, type }; setTimeout(() => { flash.value = null }, 4000) }

function initMap() {
  if (!mapContainer.value || allLocations.value.length === 0) return
  if (map) { map.remove(); map = null }
  const f = allLocations.value[0]
  map = L.map(mapContainer.value).setView([parseFloat(f.latitude), parseFloat(f.longitude)], 13)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map)
  setTimeout(() => map.invalidateSize(), 100)
  updateMarkers()
}

function updateMarkers() {
  if (!map) return
  markers.forEach(m => map.removeLayer(m)); markers = []
  allLocations.value.forEach(loc => {
    const marker = L.marker([parseFloat(loc.latitude), parseFloat(loc.longitude)]).addTo(map)
    const s = loc.shop
    const cat = s.category ? t('shops.category_' + s.category) : ''
    let popup = `<div style="min-width:160px"><strong>${s.name}</strong>`
    if (loc.name) popup += `<br><span style="color:#666;font-size:12px">${loc.name}</span>`
    if (loc.address) popup += `<br><span style="color:#999;font-size:11px">${loc.address}</span>`
    if (cat) popup += `<br><span style="color:#666">${cat}</span>`
    if (s.brands && s.brands.length) popup += `<br><span style="color:#92400e;font-size:11px">${s.brands.join(', ')}</span>`
    popup += `<br><a href="${'/shops/' + s.id}" style="color:#1d4ed8;font-size:12px;font-weight:500">${t('shops.details')}</a></div>`
    marker.bindPopup(popup)
    markers.push(marker)
  })
  if (markers.length > 1) map.fitBounds(L.featureGroup(markers).getBounds().pad(0.1))
}

function addLocation() {
  form.locations.push({ id: null, name: '', address: '', latitude: '', longitude: '' })
  nextTick(() => initFormMaps())
}

function removeLocation(idx) {
  form.locations.splice(idx, 1)
  cleanupFormMaps()
}

function initFormMaps() {
  setTimeout(() => {
    form.locations.forEach((loc, idx) => {
      const el = formMapRefs.value[idx]
      if (!el || formMaps[idx]) return
      const lat = parseFloat(loc.latitude) || 47.0105
      const lng = parseFloat(loc.longitude) || 28.8638
      const fmap = L.map(el).setView([lat, lng], 13)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(fmap)
      if (loc.latitude && loc.longitude) formMarkers[idx] = L.marker([lat, lng]).addTo(fmap)
      fmap.on('click', e => {
        form.locations[idx].latitude = e.latlng.lat.toFixed(7)
        form.locations[idx].longitude = e.latlng.lng.toFixed(7)
        if (formMarkers[idx]) formMarkers[idx].setLatLng(e.latlng)
        else formMarkers[idx] = L.marker(e.latlng).addTo(fmap)
      })
      fmap.invalidateSize()
      formMaps[idx] = fmap
    })
  }, 150)
}

function cleanupFormMaps() {
  Object.keys(formMaps).forEach(k => { if (formMaps[k]) formMaps[k].remove() })
  formMaps = {}; formMarkers = {}; formMapRefs.value = {}
  nextTick(() => initFormMaps())
}

onMounted(() => {
  if (allLocations.value.length > 0) initMap()
  const editId = new URLSearchParams(window.location.search).get('edit')
  if (editId) { const s = items.value.find(x => x.id === parseInt(editId)); if (s) openEditModal(s) }
})
watch(allLocations, () => { nextTick(() => { if (allLocations.value.length > 0) { if (!map) initMap(); else updateMarkers() } }) })

function resetForm() { Object.assign(form, { name: '', category: '', link: '', description: '', logo_url: '', brands: [], locations: [] }); brandInput.value = ''; formErrors.value = []; cleanupFormMaps() }
function openCreateModal() { editing.value = false; editingId.value = null; resetForm(); showModal.value = true }
function openEditModal(s) {
  editing.value = true; editingId.value = s.id; form.name = s.name; form.category = s.category || ''; form.link = s.link || ''; form.description = s.description || ''; form.logo_url = s.logo_url || ''
  form.brands = [...(s.brands || [])]
  brandInput.value = ''
  form.locations = (s.locations || []).map(l => ({ id: l.id, name: l.name || '', address: l.address || '', latitude: l.latitude, longitude: l.longitude }))
  formErrors.value = []; cleanupFormMaps(); showModal.value = true
}

function addBrand() {
  const raw = brandInput.value || ''
  const parts = raw.split(',').map(s => s.trim()).filter(Boolean)
  if (parts.length === 0) return
  parts.forEach(p => { if (!form.brands.includes(p)) form.brands.push(p) })
  brandInput.value = ''
}
function onBrandKey(e) {
  if (e.key === ',') { e.preventDefault(); addBrand() }
  else if (e.key === 'Backspace' && !brandInput.value && form.brands.length) { form.brands.pop() }
}
function removeBrand(idx) { form.brands.splice(idx, 1) }
function closeModal() { showModal.value = false; cleanupFormMaps() }
function confirmDelete(s) { deleteTarget.value = s; showDeleteModal.value = true }
function closeDeleteModal() { showDeleteModal.value = false; deleteTarget.value = null }

async function request(url, method, body = null) { const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }; if (body) o.body = JSON.stringify(body); return fetch(url, o) }

async function submitForm() {
  submitting.value = true; formErrors.value = []
  addBrand()
  const payload = { shop: { name: form.name, category: form.category, link: form.link, description: form.description, logo_url: form.logo_url, brands: form.brands, locations: form.locations } }
  try {
    const res = editing.value ? await request(`/shops/${editingId.value}`, 'PATCH', payload) : await request('/shops', 'POST', payload)
    if (res.ok) { const d = await res.json(); if (editing.value) { const i = items.value.findIndex(s => s.id === editingId.value); if (i !== -1) items.value[i] = d; showFlash(t('shops.updated')) } else { items.value.push(d); showFlash(t('shops.created_success')) }; closeModal() }
    else { formErrors.value = (await res.json()).errors || [t('shops.error')] }
  } catch { formErrors.value = [t('shops.error')] } finally { submitting.value = false }
}

async function approveItem(shop) {
  try {
    const res = await request(`/shops/${shop.id}/approve`, 'PATCH')
    if (res.ok) {
      const data = await res.json()
      const idx = items.value.findIndex(s => s.id === shop.id)
      if (idx !== -1) items.value[idx] = data
      showFlash(t('shops.approved'))
    }
  } catch { showFlash(t('shops.error'), 'error') }
}

async function deleteShop() {
  submitting.value = true
  try { const res = await request(`/shops/${deleteTarget.value.id}`, 'DELETE'); if (res.ok) { items.value = items.value.filter(s => s.id !== deleteTarget.value.id); showFlash(t('shops.deleted')); closeDeleteModal() } }
  catch { showFlash(t('shops.error'), 'error') } finally { submitting.value = false }
}
</script>
